<div class="container-fluid p-4">
  <div class="row">
    <div class="col-12">
      <mat-card>
        <mat-card-header>
          <mat-card-title>
            <mat-icon>cloud_sync</mat-icon>
            YourDrive Integration
          </mat-card-title>
          <mat-card-subtitle>
            External Authentication for Cross-Application Access
          </mat-card-subtitle>
        </mat-card-header>

        <mat-card-content>
          <!-- Loading Indicator -->
          <div *ngIf="isLoading" class="loading-container">
            <mat-spinner></mat-spinner>
            <p>Processing...</p>
          </div>

          <!-- Status Messages -->
          <div *ngIf="statusMessage" class="alert alert-success mt-3">
            <mat-icon>check_circle</mat-icon>
            {{ statusMessage }}
          </div>

          <div *ngIf="errorMessage" class="alert alert-danger mt-3">
            <mat-icon>error</mat-icon>
            {{ errorMessage }}
          </div>

          <!-- Firebase Token Section -->
          <mat-expansion-panel class="mt-3" [expanded]="true">
            <mat-expansion-panel-header>
              <mat-panel-title>
                <mat-icon>vpn_key</mat-icon>
                Firebase Token
              </mat-panel-title>
              <mat-panel-description>
                Your current Firebase authentication token
              </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="token-section">
              <div *ngIf="currentToken" class="token-display">
                <pre>{{ currentToken | slice:0:100 }}...</pre>
                <div class="button-group">
                  <button mat-raised-button color="primary" (click)="copyTokenToClipboard(currentToken)">
                    <mat-icon>content_copy</mat-icon>
                    Copy Token
                  </button>
                  <button mat-raised-button color="accent" (click)="verifyToken()" [disabled]="isLoading">
                    <mat-icon>verified</mat-icon>
                    Verify Token
                  </button>
                  <button mat-raised-button (click)="loadCurrentToken()" [disabled]="isLoading">
                    <mat-icon>refresh</mat-icon>
                    Refresh
                  </button>
                </div>
              </div>

              <div *ngIf="!currentToken" class="no-token">
                <mat-icon>warning</mat-icon>
                <p>No Firebase token available. Please ensure you are logged in.</p>
                <button mat-raised-button color="primary" (click)="loadCurrentToken()">
                  <mat-icon>login</mat-icon>
                  Load Token
                </button>
              </div>

              <div *ngIf="tokenVerified" class="verification-success">
                <mat-icon>check_circle</mat-icon>
                <strong>Token Verified Successfully!</strong>
              </div>
            </div>
          </mat-expansion-panel>

          <!-- YourDrive Login Section -->
          <mat-expansion-panel class="mt-3" [expanded]="true">
            <mat-expansion-panel-header>
              <mat-panel-title>
                <mat-icon>folder</mat-icon>
                YourDrive .NET API Integration
              </mat-panel-title>
              <mat-panel-description>
                Connect to YourDrive backend at {{ yourDriveApiUrl }}
              </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="yourdrive-section">
              <!-- Configuration Display -->
              <div class="config-info mb-3">
                <h4><mat-icon>settings</mat-icon> Configuration</h4>
                <div class="info-grid">
                  <div class="info-item">
                    <strong>Backend API:</strong>
                    <code>{{ yourDriveApiUrl }}</code>
                  </div>
                  <div class="info-item">
                    <strong>Frontend URL:</strong>
                    <code>{{ yourDriveUrl }}</code>
                  </div>
                </div>
              </div>

              <!-- Not Logged In State -->
              <div *ngIf="!isLoggedInToYourDrive" class="login-prompt">
                <div class="login-card">
                  <mat-icon class="large-icon">cloud_off</mat-icon>
                  <h3>Not Connected to YourDrive</h3>
                  <p>Click the button below to authenticate with YourDrive using your AccessLife credentials.</p>

                  <button
                    mat-raised-button
                    color="primary"
                    (click)="loginToYourDrive()"
                    [disabled]="isLoading || !currentToken"
                    class="large-button">
                    <mat-icon>login</mat-icon>
                    Login to YourDrive
                  </button>

                  <div *ngIf="!currentToken" class="warning-text">
                    <mat-icon>warning</mat-icon>
                    Please ensure you are logged into AccessLife first
                  </div>
                </div>
              </div>

              <!-- Logged In State -->
              <div *ngIf="isLoggedInToYourDrive" class="logged-in-state">
                <div class="status-card success">
                  <mat-icon class="status-icon">check_circle</mat-icon>
                  <div class="status-content">
                    <h3>Connected to YourDrive</h3>
                    <div class="user-details" *ngIf="yourDriveUserInfo">
                      <p><strong>Name:</strong> {{ yourDriveUserInfo.name }}</p>
                      <p><strong>Email:</strong> {{ yourDriveUserInfo.email }}</p>
                      <p><strong>User ID:</strong> {{ yourDriveUserInfo.id }}</p>
                    </div>
                  </div>
                </div>

                <div class="action-buttons">
                  <button
                    mat-raised-button
                    color="accent"
                    (click)="openYourDrive()"
                    [disabled]="isLoading">
                    <mat-icon>open_in_new</mat-icon>
                    Open YourDrive
                  </button>

                  <button
                    mat-stroked-button
                    color="warn"
                    (click)="logoutFromYourDrive()">
                    <mat-icon>logout</mat-icon>
                    Logout from YourDrive
                  </button>
                </div>

                <div class="info-box mt-3">
                  <mat-icon>info</mat-icon>
                  <div>
                    <h4>How it works:</h4>
                    <ul>
                      <li>Your Firebase token is sent to YourDrive backend</li>
                      <li>YourDrive verifies the token and creates/updates your account</li>
                      <li>A JWT token is issued for accessing YourDrive resources</li>
                      <li>Token is stored securely in localStorage</li>
                      <li>Click "Open YourDrive" to launch the application</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="button-group mt-3">
                <button mat-stroked-button color="primary" (click)="testBackend()" [disabled]="isLoading || !currentToken">
                  <mat-icon>bug_report</mat-icon>
                  Test Backend Connection
                </button>

                <button mat-stroked-button (click)="getEmbedData()" [disabled]="isLoading || !currentToken">
                  <mat-icon>code</mat-icon>
                  Get Embed Data
                </button>
              </div>

              <!-- User Data Display -->
              <div *ngIf="userData" class="user-data-section mt-4">
                <h4>
                  <mat-icon>person</mat-icon>
                  User Information
                  <mat-chip *ngIf="isNewUser" color="accent" selected>New User</mat-chip>
                </h4>

                <mat-list>
                  <mat-list-item>
                    <mat-icon matListItemIcon>badge</mat-icon>
                    <div matListItemTitle>UID</div>
                    <div matListItemLine>{{ userData.uid }}</div>
                  </mat-list-item>

                  <mat-list-item>
                    <mat-icon matListItemIcon>email</mat-icon>
                    <div matListItemTitle>Email</div>
                    <div matListItemLine>{{ userData.email }}</div>
                  </mat-list-item>

                  <mat-list-item>
                    <mat-icon matListItemIcon>person</mat-icon>
                    <div matListItemTitle>User Name</div>
                    <div matListItemLine>{{ userData.userName }}</div>
                  </mat-list-item>

                  <mat-list-item>
                    <mat-icon matListItemIcon>work</mat-icon>
                    <div matListItemTitle>Role</div>
                    <div matListItemLine>{{ userData.role }}</div>
                  </mat-list-item>

                  <mat-list-item>
                    <mat-icon matListItemIcon>business</mat-icon>
                    <div matListItemTitle>Department</div>
                    <div matListItemLine>{{ userData.department }}</div>
                  </mat-list-item>
                </mat-list>
              </div>

              <!-- Custom Token Display -->
              <div *ngIf="customToken" class="custom-token-section mt-4">
                <h4>
                  <mat-icon>security</mat-icon>
                  Custom Token (YourDrive Access)
                </h4>

                <div class="token-display">
                  <pre>{{ customToken | slice:0:100 }}...</pre>
                  <div class="button-group">
                    <button mat-raised-button color="primary" (click)="copyTokenToClipboard(customToken)">
                      <mat-icon>content_copy</mat-icon>
                      Copy Custom Token
                    </button>
                    <button mat-raised-button color="accent" (click)="refreshToken()" [disabled]="isLoading">
                      <mat-icon>refresh</mat-icon>
                      Refresh Token
                    </button>
                  </div>
                </div>

                <mat-hint>This token can be used to authenticate in YourDrive. Expires in 1 hour.</mat-hint>
              </div>
            </div>
          </mat-expansion-panel>

          <!-- Instructions Section -->
          <mat-expansion-panel class="mt-3">
            <mat-expansion-panel-header>
              <mat-panel-title>
                <mat-icon>info</mat-icon>
                How to Use
              </mat-panel-title>
              <mat-panel-description>
                Integration instructions and examples
              </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="instructions-section">
              <h4>Authentication Flow</h4>
              <ol>
                <li>Log in to AccessLife Admin (you're already logged in!)</li>
                <li>Your Firebase authentication token is automatically available</li>
                <li>Click "Login to YourDrive" to authenticate with external systems</li>
                <li>A custom token will be generated for YourDrive access</li>
                <li>Use this token to access YourDrive features</li>
              </ol>

              <h4 class="mt-3">Integration Methods</h4>
              <mat-list>
                <mat-list-item>
                  <mat-icon matListItemIcon>open_in_new</mat-icon>
                  <div matListItemTitle>Open in New Window</div>
                  <div matListItemLine>Opens YourDrive with automatic authentication</div>
                </mat-list-item>

                <mat-list-item>
                  <mat-icon matListItemIcon>web</mat-icon>
                  <div matListItemTitle>Embed in iFrame</div>
                  <div matListItemLine>Get embed data to integrate YourDrive in this page</div>
                </mat-list-item>

                <mat-list-item>
                  <mat-icon matListItemIcon>api</mat-icon>
                  <div matListItemTitle>API Integration</div>
                  <div matListItemLine>Use the custom token for API calls to YourDrive</div>
                </mat-list-item>
              </mat-list>

              <h4 class="mt-3">Security Notes</h4>
              <ul>
                <li>Firebase tokens expire after 1 hour</li>
                <li>Custom tokens also expire after 1 hour</li>
                <li>Use the refresh button to get a new token when expired</li>
                <li>Never share your tokens with unauthorized users</li>
                <li>Tokens are transmitted over HTTPS only</li>
              </ul>
            </div>
          </mat-expansion-panel>

          <!-- Actions -->
          <div class="actions-section mt-4">
            <button mat-raised-button color="warn" (click)="clearData()">
              <mat-icon>clear_all</mat-icon>
              Clear All Data
            </button>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </div>
</div>

